/**
 * Bug Reporting — frontend API client.
 *
 * Spec:  docs/superpowers/specs/2026-05-07-bug-reporting-design.md
 * Plan:  docs/superpowers/plans/2026-05-07-bug-reporting.md
 */
import { api } from './api';
import { supabase } from './supabase';

export type BugStatus =
  | 'new'
  | 'triaging'
  | 'in_progress'
  | 'fixed_in_dev'
  | 'verified'
  | 'wont_fix'
  | 'needs_info'
  | 'duplicate';

export type BugSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface BugReport {
  id: string;
  reporter_id: string;
  reporter_role: string;
  org_id: string;
  title: string;
  description: string;
  page_url: string | null;
  user_agent: string | null;
  viewport_size: string | null;
  screenshot_path: string | null;
  screenshot_url?: string | null;
  severity: BugSeverity | null;
  status: BugStatus;
  fix_pr_url: string | null;
  fix_branch: string | null;
  duplicate_of: string | null;
  recurring_count: number;
  created_at: string;
  updated_at: string;
  comments?: BugComment[];
}

export interface BugComment {
  id: string;
  bug_id: string;
  author_id: string | null;
  author_kind: 'user' | 'admin' | 'super-admin' | 'claude';
  body: string;
  internal: boolean;
  created_at: string;
  screenshot_path?: string | null;
}

export interface CreateBugInput {
  title: string;
  description: string;
  page_url?: string;
  user_agent?: string;
  viewport_size?: string;
  screenshot_path?: string;
}

export async function createBug(input: CreateBugInput): Promise<BugReport> {
  const { data } = await api.post<{ bug: BugReport }>('/api/bugs', input);
  return data.bug;
}

export interface ListBugsParams {
  status?: BugStatus[];
  severity?: BugSeverity[];
  search?: string;
  org_id?: string;
  mine?: boolean;
  /** Only bugs with recurring_count > 0; excludes already-verified / fixed_in_dev. */
  recurring?: boolean;
  limit?: number;
  offset?: number;
}

export async function listBugs(
  params: ListBugsParams = {},
): Promise<{ bugs: BugReport[]; total: number }> {
  const query: Record<string, string> = {};
  if (params.status?.length)   query.status   = params.status.join(',');
  if (params.severity?.length) query.severity = params.severity.join(',');
  if (params.search)           query.search   = params.search;
  if (params.org_id)           query.org_id   = params.org_id;
  if (params.mine)             query.mine     = 'true';
  if (params.recurring)        query.recurring = 'true';
  if (params.limit !== undefined)  query.limit  = String(params.limit);
  if (params.offset !== undefined) query.offset = String(params.offset);

  const { data } = await api.get<{ bugs: BugReport[]; total: number }>('/api/bugs', {
    params: query,
  });
  return data;
}

export async function getBug(id: string): Promise<BugReport> {
  const { data } = await api.get<{ bug: BugReport }>(`/api/bugs/${id}`);
  return data.bug;
}

export async function postComment(
  bugId: string,
  body: string,
  internal = false,
): Promise<BugComment> {
  const { data } = await api.post<{ comment: BugComment }>(
    `/api/bugs/${bugId}/comments`,
    { body, internal },
  );
  return data.comment;
}

export interface PatchBugInput {
  status?: BugStatus;
  severity?: BugSeverity;
  fix_pr_url?: string | null;
  fix_branch?: string | null;
  duplicate_of?: string | null;
}

export async function patchBug(id: string, updates: PatchBugInput): Promise<BugReport> {
  const { data } = await api.patch<{ bug: BugReport }>(`/api/bugs/${id}`, updates);
  return data.bug;
}

export async function reopenBug(id: string, note: string): Promise<BugReport> {
  const { data } = await api.post<{ bug: BugReport }>(`/api/bugs/${id}/reopen`, { note });
  return data.bug;
}

export interface RecurBugInput {
  note: string;
  screenshot_path?: string;
}

/**
 * Flag an additional occurrence of an existing bug. Bumps recurring_count;
 * if the bug was closed (verified/wont_fix) the server moves it to triaging.
 * Reporter-only for own bugs; admins can flag any bug in their org.
 */
export async function recurBug(id: string, input: RecurBugInput): Promise<BugReport> {
  const { data } = await api.post<{ bug: BugReport }>(`/api/bugs/${id}/recur`, input);
  return data.bug;
}

/**
 * Request a signed upload URL, then PUT the screenshot blob to it.
 * Returns the storage path that should be passed as `screenshot_path` on
 * the subsequent createBug call.
 */
export async function uploadScreenshot(blob: Blob): Promise<string> {
  const { data } = await api.post<{ upload_url: string; path: string }>(
    '/api/bugs/screenshot-upload',
    { filename: 'screenshot.png' },
  );

  const upload = await fetch(data.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/png' },
    body: blob,
  });
  if (!upload.ok) {
    throw new Error(`Screenshot upload failed: ${upload.status}`);
  }
  return data.path;
}

/**
 * Subscribe to realtime changes on bugs for the current user.
 * The callback fires for INSERT/UPDATE/DELETE on bug_reports rows visible to
 * the caller (RLS scopes this automatically).
 */
export function subscribeToBugs(callback: () => void): () => void {
  const channel = supabase
    .channel('bug_reports_changes')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'bug_reports' },
      callback,
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'bug_comments' },
      callback,
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
